import type {
  MarkBookingAttendanceArgs,
  MarkBookingPaidArgs,
  FetchBookingEventByIdArgs,
  FetchLastCancellationPolicyArgs,
  CreateBookingEventResult,
  ApiBookingEventWarning,
  CreateBookingEventArgs,
  UpdateBookingEventArgs,
  DeleteBookingEventArgs,
  BookingEventActionArgs,
  FetchBookingEventsArgs,
  ApiBookingEventDetail,
  ApiBookingVipLink,
  ApiBookingRoster,
  ApiRosterSeat,
  ApiBookingEvent,
} from './types/bookingEvents.types';
import type { BaseApiResponse } from './types/api.types';

import { destroy, patch, post, get } from './axios';
import { dayRangeQuery } from './dayRangeQuery';

const eventsUrl = (organizationId: string) =>
  `/v2/organizations/${organizationId}/booking-events`;

export const fetchBookingEvents = async ({
  clientUserId,
  from,
  includeCancelled,
  organizationId,
  timeZone,
  to,
  trainerUserId,
}: FetchBookingEventsArgs) => {
  const response = await get<BaseApiResponse<ApiBookingEvent[]>>(
    eventsUrl(organizationId),
    {
      query: {
        ...dayRangeQuery(from, to, timeZone),
        ...(trainerUserId ? { trainerUserId } : {}),
        ...(clientUserId ? { clientUserId } : {}),
        ...(includeCancelled ? { includeCancelled: 'true' } : {}),
      },
    },
  );
  return response?.data ?? [];
};

export const fetchBookingEventById = async ({
  bookingEventId,
  organizationId,
}: FetchBookingEventByIdArgs) => {
  const response = await get<BaseApiResponse<ApiBookingEventDetail>>(
    `${eventsUrl(organizationId)}/${bookingEventId}`,
  );
  return response?.data ?? null;
};

/**
 * Creating a recurring event returns EVERY materialized occurrence, not just
 * the first — the trainer asked for a series and the calendar has to be able to
 * show what they actually got.
 *
 * A trainer-overlap conflict pauses the write (`requiresConfirmation: true`,
 * `events: null`) instead — `requiresConfirmation`/`data: null` never reach the
 * caller through the typed `data` field, so both are pulled off the raw
 * response the same way `assignBookingEventTemplate` does.
 */
export const createBookingEvent = async ({
  organizationId,
  ...payload
}: CreateBookingEventArgs): Promise<CreateBookingEventResult> => {
  const response = await post<
    ApiBookingEvent[] | null,
    Omit<CreateBookingEventArgs, 'organizationId'>
  >(eventsUrl(organizationId), payload);
  const raw = response as unknown as {
    requiresConfirmation?: boolean;
    warnings?: ApiBookingEventWarning[];
    data?: ApiBookingEvent[] | null;
  };
  return {
    events: raw?.data ?? null,
    requiresConfirmation: raw?.requiresConfirmation ?? false,
    warnings: raw?.warnings ?? [],
  };
};

export const updateBookingEvent = async ({
  bookingEventId,
  organizationId,
  ...payload
}: UpdateBookingEventArgs) => {
  const response = await patch<
    ApiBookingEvent[],
    Omit<UpdateBookingEventArgs, 'bookingEventId' | 'organizationId'>
  >(`${eventsUrl(organizationId)}/${bookingEventId}`, payload);
  return {
    events: response?.data ?? [],
    warnings:
      (response as unknown as { warnings?: ApiBookingEventWarning[] })
        ?.warnings ?? [],
  };
};

/**
 * An event nobody booked is deleted outright; one with bookings is cancelled
 * and every seat holder is notified (with `message` appended, when given). The
 * caller does not choose which — the API decides from the bookings.
 *
 * The scope and the note travel in the request BODY, so a free-text message
 * does not have to survive a URL.
 */
export const deleteBookingEvent = async ({
  bookingEventId,
  message,
  organizationId,
  scope,
}: DeleteBookingEventArgs) => {
  await destroy(`${eventsUrl(organizationId)}/${bookingEventId}`, {
    // `destroy`'s config is typed for a body-less DELETE; axios does send
    // `data` on DELETE, and this endpoint needs it (free-text note + scope).
    data: {
      ...(scope ? { scope } : {}),
      ...(message ? { message } : {}),
    },
  } as unknown as Parameters<typeof destroy>[1]);
};

export const fetchBookingVipLink = async ({
  bookingEventId,
  organizationId,
}: BookingEventActionArgs) => {
  const response = await get<BaseApiResponse<ApiBookingVipLink>>(
    `${eventsUrl(organizationId)}/${bookingEventId}/vip-link`,
  );
  return response?.data ?? null;
};

export const rotateBookingVipLink = async ({
  bookingEventId,
  organizationId,
}: BookingEventActionArgs) => {
  const response = await post<ApiBookingVipLink>(
    `${eventsUrl(organizationId)}/${bookingEventId}/vip-link/rotate`,
  );
  return response?.data ?? null;
};

/** One-way: unused privileged seats become ordinary ones and the queue is fed. */
export const releaseBookingVipSeats = async ({
  bookingEventId,
  organizationId,
}: BookingEventActionArgs) => {
  const response = await post<{
    waitlistOffersSent: number;
    event: ApiBookingEvent;
  }>(`${eventsUrl(organizationId)}/${bookingEventId}/release-vip-seats`);
  return response?.data ?? null;
};

/**
 * Attendance only — the session was spent when the seat was taken, so this
 * never charges a second one.
 */
export const markBookingAttendance = async ({
  bookingId,
  notes,
  organizationId,
  status,
}: MarkBookingAttendanceArgs) => {
  const response = await post<ApiRosterSeat>(
    `/v2/organizations/${organizationId}/bookings/${bookingId}/attendance`,
    { notes, status },
  );
  return response?.data ?? null;
};

/**
 * Front desk/admin settling a walk-in after the fact — never a client
 * self-service action (see CreateBookingsDto on the backend).
 */
export const markBookingPaid = async ({
  bookingId,
  method,
  notes,
  organizationId,
}: MarkBookingPaidArgs) => {
  const response = await patch<ApiBookingRoster>(
    `/v2/organizations/${organizationId}/bookings/${bookingId}/mark-paid`,
    { method, notes },
  );
  return response?.data ?? null;
};

/**
 * The default the create-event form pre-fills with when no template was
 * picked — "the reference for the cancellation policy is always the
 * last-set rule".
 */
export const fetchLastBookingCancellationPolicy = async ({
  organizationId,
}: FetchLastCancellationPolicyArgs) => {
  const response = await get<
    BaseApiResponse<{ cancellationDeadlineHours: number }>
  >(`${eventsUrl(organizationId)}/last-cancellation-policy`);
  return response?.data ?? null;
};
